import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Stepper } from '@enamelai/design-system'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { purchaseTwilioNumberAction } from '@store/actions/settings-communication.action'
import { clearSettingsCommunicationError } from '@store/reducers/settings-communication.reducer'
import './PurchaseNumberStyle.css'
import { IClinicNumber, ITwilioAvailableNumber } from '@dataTypes/clinic.type'
import { SelectNumber, ReviewInfo } from './Steps'
import {
  getAvailablePurchaseNumbersExternal,
  getPurchaseNumberPricingExternal,
} from '@services/api/phoneManagement.api'
import { usePhoneManagementCtx } from '../PhoneManagement.context'

const defaultNumberData: IClinicNumber = {
  clinic_number_id: '',
  clinic_id: '',
  is_verified: false,
  is_portable: false,
  phone_number: '',
  phone_carrier: '',
  number_sid: '',
  status: '',
  purchased_date: '',
}

type Props = {
  show: boolean
  onHide: () => void
  initialPhone?: string
}

const PurchaseNumberModal = ({
  show,
  onHide,
  initialPhone = '',
}: Props): React.ReactElement => {
  const [step, setStep] = useState(0)
  const [numberData, setNumberData] = useState<IClinicNumber>(defaultNumberData)
  const [selectedType, setSelectedType] = useState<
    'local' | 'toll-free' | null
  >(null)
  const [pricing, setPricing] = useState<{
    local_monthly_price: number
    toll_free_monthly_price: number
    currency: string
  } | null>(null)
  const [availableNumbers, setAvailableNumbers] = useState<
    ITwilioAvailableNumber[]
  >([])
  const [selectedNumber, setSelectedNumber] =
    useState<ITwilioAvailableNumber | null>(null)
  const [isPricingLoading, setIsPricingLoading] = useState(false)
  const [isNumbersLoading, setIsNumbersLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const { loading, error } = useAppSelector(
    (state) => state.settingsCommunication,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { runAsync } = usePhoneManagementCtx()

  const steps = ['Select number', 'Review info']

  useEffect(() => {
    if (!show) return
    setStep(0)
    setSelectedType(null)
  }, [show, initialPhone])

  const countryCode = selectedClinic?.country_code || 'US'
  const longitude = selectedClinic?.coordinates?.lng
  const latitude = selectedClinic?.coordinates?.lat
  const sid = selectedClinic?.twilio_account_sid?.trim() || ''
  const token = selectedClinic?.twilio_auth_token?.trim() || ''

  const reset = (): void => {
    setStep(0)
    setNumberData(defaultNumberData)
    setSelectedType(null)
    setAvailableNumbers([])
    setPricing(null)
    setFetchError(null)
    dispatch(clearSettingsCommunicationError())
  }

  const handleClose = (): void => {
    reset()
    onHide()
  }

  const handleChange = (
    field: string,
    value: string | boolean | null,
  ): void => {
    const updatedData: Partial<IClinicNumber> = { [field]: value }
    setNumberData((prev) => ({ ...prev, ...updatedData }))
  }

  const loadPricing = useCallback(async (): Promise<void> => {
    if (!sid || !token) {
      setFetchError('Missing Twilio credentials for active clinic.')
      return
    }
    setIsPricingLoading(true)
    try {
      const res = await getPurchaseNumberPricingExternal(
        countryCode,
        sid,
        token,
      )
      if (res.statusCode >= 400) {
        setFetchError(res.message || 'Failed to load Twilio pricing.')
        return
      }
      setPricing({
        local_monthly_price: res.data?.local_monthly_price ?? 0,
        toll_free_monthly_price: res.data?.toll_free_monthly_price ?? 0,
        currency: res.data?.currency || 'USD',
      })
      setFetchError(null)
    } finally {
      setIsPricingLoading(false)
    }
  }, [countryCode, sid, token])

  const loadAvailableNumbers = useCallback(
    async (numberType: 'local' | 'toll-free' | null): Promise<void> => {
      if (!numberType) {
        setFetchError('Please select a number type.')
        return
      }
      if (!sid || !token) {
        setFetchError('Missing Twilio credentials for active clinic.')
        return
      }
      setIsNumbersLoading(true)
      try {
        if (longitude && latitude) {
          const res = await getAvailablePurchaseNumbersExternal({
            numberType,
            countryCode,
            longitude: longitude.toString(),
            latitude: latitude.toString(),
            sid,
            token,
          })
          if (res.statusCode >= 400) {
            setFetchError(res.message || 'Failed to load available numbers.')
            setAvailableNumbers([])
            return
          }
          setAvailableNumbers(res.data?.available_numbers || [])
          setFetchError(null)
        }
      } finally {
        setIsNumbersLoading(false)
      }
    },
    [countryCode, longitude, latitude, sid, token],
  )

  useEffect(() => {
    if (!show) return
    void loadPricing()
  }, [show, loadPricing])

  useEffect(() => {
    if (!show || selectedType === null) return
    void loadAvailableNumbers(selectedType)
  }, [show, selectedType, loadAvailableNumbers])

  const canProceedToVerification = useMemo(() => {
    return !!numberData.phone_number
  }, [numberData.phone_number])

  const onNumberTypeChange = (type: 'local' | 'toll-free' | null): void => {
    setSelectedType(type)
    setNumberData((prev) => ({ ...prev, phone_number: '' }))
  }

  const handleNext = (): void => {
    if (!canProceedToVerification) return
    const nextStep = step + 1
    setStep(nextStep)
  }

  const handlePrevious = (): void => setStep(step - 1)

  const getSectionComponent = (): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <SelectNumber
            numberData={numberData}
            handleChange={handleChange}
            selectedType={selectedType}
            setSelectedType={onNumberTypeChange}
            pricing={pricing}
            isPricingLoading={isPricingLoading}
            availableNumbers={availableNumbers}
            isNumbersLoading={isNumbersLoading}
            fetchError={fetchError}
            selectedNumber={selectedNumber}
            setSelectedNumber={setSelectedNumber}
          />
        )
      case 1:
        return (
          <ReviewInfo
            numberData={numberData}
            price={
              selectedType === 'local'
                ? pricing?.local_monthly_price.toString() || '0.00'
                : pricing?.toll_free_monthly_price.toString() || '0.00'
            }
          />
        )
      default:
        return <></>
    }
  }

  const handleConfirmProceed = (): void => {
    const clinicId = selectedClinic?.clinic_id
    const phoneNumber = numberData.phone_number?.trim()
    if (!phoneNumber || !clinicId || !sid || !token) return

    const friendlyName = `${selectedClinic?.clinic_name ?? ''}-${phoneNumber}`
      .split(' ')
      .join('_')
      .toLowerCase()

    void runAsync(
      async () => {
        await dispatch(
          purchaseTwilioNumberAction({
            phoneNumber,
            friendlyName,
            sid,
            token,
            clinicId,
            contactMethods: numberData.contact_methods ?? null,
          }),
        ).unwrap()
        handleClose()
      },
      {
        successTitle: 'Number purchased',
        successMessage: 'The phone number was added to your clinic.',
        errorTitle: 'Could not purchase number',
        errorFallbackMessage:
          'Unable to complete the purchase. Please try again.',
      },
    )
  }

  const handlePurchaseRetry = (): void => {
    dispatch(clearSettingsCommunicationError())
    setStep(0)
  }

  const isReviewStep = step === steps.length - 1

  return (
    <Modal
      open={show}
      onClose={handleClose}
      closeOnBackdrop={false}
      className="scp-pm-purchase-modal"
      size="md"
      title="Purchase number"
      primaryLabel={
        isReviewStep && error ? 'Retry' : isReviewStep ? 'Proceed' : 'Next'
      }
      secondaryLabel={step === 0 ? 'Cancel' : 'Previous'}
      primaryDisabled={loading || (step === 0 && !canProceedToVerification)}
      onPrimaryClick={() => {
        if (isReviewStep && error) {
          handlePurchaseRetry()
          return
        }
        if (isReviewStep) {
          handleConfirmProceed()
          return
        }
        handleNext()
      }}
      onSecondaryClick={() => {
        if (step === 0) {
          handleClose()
          return
        }
        handlePrevious()
      }}
    >
      {step < steps.length ? (
        <Stepper steps={steps} currentStep={step} />
      ) : null}
      {error && step > 0 && step < steps.length ? (
        <p className="hint-text">{error}</p>
      ) : null}
      <div key={step} className="w-100">
        {getSectionComponent()}
      </div>
    </Modal>
  )
}

export default PurchaseNumberModal
